import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FlaskConical, Save, RotateCcw, Sparkles, AlertTriangle } from "lucide-react";

/**
 * /admin/simulation — Dry-run sandbox controls.
 * Snapshot live → seed test tournament → dry-run → restore snapshot.
 */
export default function SimulationDashboard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);
  const [preset, setPreset] = useState("playoffs-ready");

  const load = async () => {
    try {
      const { data } = await api.get("/simulation/status");
      setStatus(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load status");
    }
  };
  useEffect(() => { load(); }, []);

  const doAction = async (path, label, warn) => {
    if (warn && !window.confirm(warn)) return;
    setBusy(label);
    try {
      let url;
      if (path === "seed") {
        url = `/simulation/seed?preset=${encodeURIComponent(preset)}&confirm=true`;
      } else {
        url = `/simulation/${path}`;
      }
      const { data } = await api.post(url);
      toast.success(`${label}: ${JSON.stringify(data).slice(0, 120)}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${label} failed`);
    } finally { setBusy(null); }
  };

  return (
    <div className="p-6 md:p-8 min-h-screen bg-[#060d09] text-white" data-testid="simulation-dashboard">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <FlaskConical className="w-5 h-5 text-[#F4B400]" />
          <h1 className="font-anton text-4xl uppercase tracking-tight">Simulation / Sandbox</h1>
        </div>
        <p className="text-sm text-zinc-400 max-w-2xl">
          Snapshot the current live state, then wipe & seed a canonical Season-3 test tournament (3 groups × 4 teams + finished matches). Dry-run the full flow (lineups → reveal → scoring → GD → wildcard → QF → bracket). Restore the snapshot when done.
        </p>
      </div>

      <Card className="p-4 mb-4 bg-amber-500/10 border-amber-500/40 flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />
        <div className="text-xs text-amber-100">
          <b>Warning:</b> Seeding replaces teams, players, matches, lineups and ties. Always <b>Snapshot</b> before <b>Seed</b>.
        </div>
      </Card>

      {status && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card className="p-5 bg-[#0f1a15] border-white/10" data-testid="sim-live-panel">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Live Counts</div>
            {Object.entries(status.live_counts || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between py-1 text-sm">
                <span className="text-zinc-300">{k}</span>
                <span className="font-anton text-xl tabular-nums">{v}</span>
              </div>
            ))}
          </Card>
          <Card className="p-5 bg-[#0f1a15] border-white/10" data-testid="sim-snapshot-panel">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Snapshot</div>
            {status.snapshot_exists ? (
              <>
                <div className="text-sm text-emerald-400 mb-2">✓ Snapshot available</div>
                <div className="text-[10px] text-zinc-500 mb-3">Taken {new Date(status.snapshot_created_at).toLocaleString()}</div>
                {Object.entries(status.snapshot_counts || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between py-1 text-xs text-zinc-400">
                    <span>{k}</span><span className="tabular-nums">{v}</span>
                  </div>
                ))}
              </>
            ) : (
              <div className="text-sm text-zinc-500 italic">No snapshot yet.</div>
            )}
          </Card>
        </div>
      )}

      <Card className="p-4 mb-4 bg-[#0f1a15] border-white/10" data-testid="sim-presets-panel">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Seed Preset</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
          {[
            { key: "playoffs-ready", label: "Playoffs Ready", desc: "12 teams · full round-robin finished · ready for QF publish" },
            { key: "group-stage-mid", label: "Group Stage Mid", desc: "12 teams · half the group ties played · lineups in flight" },
            { key: "final-day-only", label: "Final Day Only", desc: "12 teams · groups finished · QF/SF/F placeholders scheduled" },
            { key: "live-in-progress", label: "Live In Progress", desc: "12 teams · one Court 1 tennis match mid-set at DEUCE · perfect for demos" },
          ].map((p) => (
            <button
              key={p.key}
              onClick={() => setPreset(p.key)}
              data-testid={`sim-preset-${p.key}`}
              className={`text-left p-3 rounded-lg border transition ${
                preset === p.key
                  ? "border-[#F4B400] bg-[#F4B400]/10"
                  : "border-white/10 bg-black/30 hover:border-white/30"
              }`}
            >
              <div className="text-sm font-bold uppercase tracking-widest text-white">{p.label}</div>
              <div className="text-[10px] text-zinc-400 mt-1 leading-snug">{p.desc}</div>
            </button>
          ))}
        </div>
        <div className="text-[10px] text-zinc-500 mt-2">Active preset: <b className="text-[#F4B400]">{preset}</b> — used by the Seed Test Tournament button below.</div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Button onClick={() => doAction("snapshot", "Snapshot")} disabled={busy === "Snapshot"}
          className="h-24 bg-[#00A86B] hover:bg-[#00c17b] text-black font-bold uppercase tracking-widest"
          data-testid="sim-snapshot-btn">
          <Save className="w-5 h-5 mr-2" /> {busy === "Snapshot" ? "Saving…" : "Snapshot Live"}
        </Button>
        <Button onClick={() => doAction("seed", "Seed", "This will WIPE current teams / players / matches / lineups. Snapshot first!")}
          disabled={busy === "Seed"}
          className="h-24 bg-[#F4B400] hover:bg-[#e5aa00] text-black font-bold uppercase tracking-widest"
          data-testid="sim-seed-btn">
          <Sparkles className="w-5 h-5 mr-2" /> {busy === "Seed" ? "Seeding…" : "Seed Test Tournament"}
        </Button>
        <Button onClick={() => doAction("restore", "Restore", "Restore the saved snapshot? This wipes current tournament data.")}
          disabled={busy === "Restore" || !status?.snapshot_exists}
          className="h-24 bg-red-600 hover:bg-red-500 text-white font-bold uppercase tracking-widest disabled:opacity-40"
          data-testid="sim-restore-btn">
          <RotateCcw className="w-5 h-5 mr-2" /> {busy === "Restore" ? "Restoring…" : "Restore Snapshot"}
        </Button>
      </div>
    </div>
  );
}
